import { useEffect, useState } from "react";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BOOKING_COPY = {
  en: {
    title: "Book your place",
    sub: "Tell us who you are and choose your date — payment happens securely on the next step via Stripe (iDEAL or card).",
    dateLabel: "Which date?",
    ticketLabel: "Who is coming?",
    name: "Your name",
    email: "Email",
    phone: "Phone (optional)",
    submit: "Continue to secure payment",
    note: "You'll be taken to a secure Stripe payment page. Your place is confirmed once payment completes — a personal confirmation email from Irida follows.",
    soldOut: "This date is now full.",
    fullTag: "full",
    notEnough: "(only 1 place left)",
    soldOutTag: "sold out — waiting list open",
    placesLeft: (n) => `${n} ${n === 1 ? "place" : "places"} left`,
    refund: {
      title: "Cancellations & refunds",
      intro: "I understand that plans can change. If you need to cancel your place, please let me know as soon as you can.",
      items: [
        { h: "7 days or more before the start date", b: "You can receive a full refund, or transfer your booking to another available date." },
        { h: "Less than 7 days before the start date", b: "Bookings are non-refundable. Where possible, you may transfer your place to another available date or to another person." },
      ],
      itemsWomensCircle: [
        { h: "3 days or more before the circle", b: "You can receive a full refund, or transfer your booking to another available circle date." },
        { h: "Less than 3 days before the circle", b: "Bookings are non-refundable. Where possible, you may transfer your place to another circle date or to another woman." },
      ],
      circle: { h: "Mums & Babies Circle", b: "Your booking is for the full six-week circle. Once the circle has started, missed individual sessions are not refundable. If you need to cancel before the programme begins, the cancellation terms above apply." },
      itemsAfter: [
        { h: "If Women's Den cancels or reschedules", b: "If I need to cancel an event, you will receive a full refund or, if you prefer, transfer your booking to another available date. If an event is rescheduled and the new date doesn't work for you, you can receive a full refund." },
        { h: "Exceptional circumstances", b: "Pregnancy, birth and family life don't always unfold according to plan. If something unexpected means you genuinely cannot attend, please get in touch. Exceptional circumstances can be considered individually." },
      ],
      outro: "Refunds are returned to the original payment method. Any approved refund will be processed as soon as possible, although the time it takes to appear in your account depends on your payment provider.",
      statutory: "Nothing in this cancellation policy affects any statutory rights you may have as a consumer. Where a statutory right of withdrawal applies to your booking, information about that right and how to exercise it is set out in our Terms & Conditions.",
    },
    waitlistCta: "Join the waiting list",
    waitlistDone: "You're on the list — we'll write if a place opens.",
    error: "Something went wrong — please try again, or reach out via the contact page.",
  },
  nl: {
    title: "Reserveer je plek",
    sub: "Vertel wie je bent en kies je datum — de betaling verloopt daarna veilig via Stripe (iDEAL of kaart).",
    dateLabel: "Welke datum?",
    ticketLabel: "Wie komen er?",
    name: "Je naam",
    email: "E-mail",
    phone: "Telefoon (optioneel)",
    submit: "Ga verder naar veilige betaling",
    note: "Je wordt doorgestuurd naar een beveiligde Stripe-betaalpagina. Je plek is bevestigd zodra de betaling is voltooid — een persoonlijke bevestigingsmail van Irida volgt.",
    soldOut: "Deze datum is inmiddels vol.",
    fullTag: "vol",
    notEnough: "(nog maar 1 plek)",
    soldOutTag: "vol — wachtlijst open",
    placesLeft: (n) => `nog ${n} ${n === 1 ? "plek" : "plekken"}`,
    refund: {
      title: "Annuleren & terugbetalingen",
      intro: "Ik begrijp dat plannen kunnen veranderen. Moet je je plek annuleren, laat het me dan zo snel mogelijk weten.",
      items: [
        { h: "7 dagen of meer voor de startdatum", b: "Je kunt een volledige terugbetaling ontvangen, of je boeking verplaatsen naar een andere beschikbare datum." },
        { h: "Minder dan 7 dagen voor de startdatum", b: "Boekingen zijn niet terugbetaalbaar. Waar mogelijk kun je je plek verplaatsen naar een andere beschikbare datum of aan iemand anders overdragen." },
      ],
      itemsWomensCircle: [
        { h: "3 dagen of meer voor de cirkel", b: "Je kunt een volledige terugbetaling ontvangen, of je boeking verplaatsen naar een andere beschikbare cirkeldatum." },
        { h: "Minder dan 3 dagen voor de cirkel", b: "Boekingen zijn niet terugbetaalbaar. Waar mogelijk kun je je plek verplaatsen naar een andere cirkeldatum of aan een andere vrouw overdragen." },
      ],
      circle: { h: "Mums & Babies Circle", b: "Je boeking geldt voor de volledige zesweekse kring. Zodra de kring is begonnen, zijn gemiste individuele bijeenkomsten niet terugbetaalbaar. Moet je annuleren voordat het programma begint, dan gelden de bovenstaande annuleringsvoorwaarden." },
      itemsAfter: [
        { h: "Als Women's Den annuleert of verplaatst", b: "Als ik een evenement moet annuleren, ontvang je een volledige terugbetaling of, als je dat liever wilt, verplaatsen we je boeking naar een andere beschikbare datum. Als een evenement wordt verplaatst en de nieuwe datum niet past, kun je een volledige terugbetaling ontvangen." },
        { h: "Uitzonderlijke omstandigheden", b: "Zwangerschap, geboorte en gezinsleven verlopen niet altijd volgens plan. Als iets onverwachts ervoor zorgt dat je echt niet aanwezig kunt zijn, neem dan contact op. Uitzonderlijke omstandigheden kunnen individueel worden bekeken." },
      ],
      outro: "Terugbetalingen gaan terug naar de oorspronkelijke betaalmethode. Een goedgekeurde terugbetaling wordt zo snel mogelijk verwerkt; hoe lang het duurt voordat het op je rekening staat, hangt af van je betaalprovider.",
      statutory: "Niets in dit annuleringsbeleid doet afbreuk aan wettelijke rechten die je als consument hebt. Wanneer een wettelijk herroepingsrecht van toepassing is op je boeking, vind je informatie over dat recht en hoe je het uitoefent in onze Algemene Voorwaarden.",
    },
    waitlistCta: "Zet me op de wachtlijst",
    waitlistDone: "Je staat op de lijst — we schrijven je als er een plek vrijkomt.",
    error: "Er ging iets mis — probeer het opnieuw, of neem contact op via de contactpagina.",
  },
};

const inputCls = "w-full rounded-none border-b border-ink/20 bg-transparent px-0 py-3 text-base text-ink placeholder:text-soft/50 focus:border-clay focus:outline-none transition-colors duration-300";
const labelCls = "text-xs tracking-[0.25em] uppercase text-soft";

export const BookingForm = ({ offer, dates, tickets, priceLabel }) => {
  const { lang } = useLang();
  const c = BOOKING_COPY[lang] || BOOKING_COPY.en;
  const [form, setForm] = useState({
    date: dates.length === 1 ? dates[0].value : "",
    ticket: tickets ? tickets[0].value : "",
    name: "", email: "", phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [soldOut, setSoldOut] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistWebsite, setWaitlistWebsite] = useState(""); // honeypot
  const [waitlistDone, setWaitlistDone] = useState(false);
  const [availability, setAvailability] = useState(null);

  const fetchAvailability = () =>
    fetch(`${API}/bookings/availability/${offer}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setAvailability(d.remaining))
      .catch(() => {});

  useEffect(() => { fetchAvailability(); }, [offer]);

  // If the selected ticket no longer fits the remaining places, switch to one that does
  useEffect(() => {
    if (!tickets || !availability || !form.date) return;
    const seatsFor = (v) => (offer === "active-birth" && v === "couple" ? 2 : 1);
    const left = availability[form.date] ?? 0;
    if (left < seatsFor(form.ticket)) {
      const fitting = tickets.find((t) => seatsFor(t.value) <= left);
      if (fitting && fitting.value !== form.ticket) setForm((f) => ({ ...f, ticket: fitting.value }));
    }
  }, [availability, form.date, form.ticket]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/bookings/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          offer, date: form.date, ticket: form.ticket || undefined,
          name: form.name, email: form.email, phone: form.phone || undefined,
          lang,
        }),
      });
      if (res.status === 409) {
        fetchAvailability();
        setSoldOut(true);
        return;
      }
      if (!res.ok) throw new Error("checkout failed");
      const data = await res.json();
      window.location.href = data.checkout_url;
    } catch {
      toast.error(c.error);
    } finally {
      setSubmitting(false);
    }
  };

  const joinWaitlist = async (e) => {
    e.preventDefault();
    try {
      await fetch(`${API}/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: waitlistEmail, workshop: `${offer}-${form.date || dates[0].value}`, lang, website: waitlistWebsite }),
      });
      setWaitlistDone(true);
    } catch {
      toast.error(c.error);
    }
  };

  if (soldOut) {
    return (
      <div data-testid="booking-soldout">
        <p className="font-serif text-2xl text-ink">{c.soldOut}</p>
        {waitlistDone ? (
          <p className="mt-4 text-sm text-clay" data-testid="booking-waitlist-done">{c.waitlistDone}</p>
        ) : (
          <form onSubmit={joinWaitlist} className="mt-6 flex flex-wrap items-end gap-4">
            <input type="email" required value={waitlistEmail} onChange={(e) => setWaitlistEmail(e.target.value)}
              placeholder={c.email} aria-label={c.email} className={`${inputCls} max-w-xs`} data-testid="booking-waitlist-email" />
            <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
              <input type="text" name="website" tabIndex={-1} autoComplete="off" value={waitlistWebsite} onChange={(e) => setWaitlistWebsite(e.target.value)} data-testid="booking-waitlist-honeypot" />
            </div>
            <button type="submit" data-testid="booking-waitlist-submit"
              className="group inline-flex items-center gap-3 rounded-full border border-clay px-6 py-3 text-sm tracking-wide text-clay transition-colors duration-300 hover:bg-clay hover:text-cream">
              {c.waitlistCta}
            </button>
          </form>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="booking-form">
      <p className="text-sm leading-relaxed text-soft max-w-xl">{c.sub}</p>
      <div className="mt-8 grid gap-6 sm:grid-cols-2">
        {dates.length > 1 && (
          <div>
            <label htmlFor={`${offer}-date`} className={labelCls}>{c.dateLabel}</label>
            <select id={`${offer}-date`} required value={form.date} onChange={set("date")} className={`${inputCls} cursor-pointer`} data-testid="booking-date-select">
              <option value="" disabled>—</option>
              {dates.map((d) => {
                const full = availability && availability[d.value] === 0;
                return (
                  <option key={d.value} value={d.value} disabled={full} data-testid={`booking-date-option-${d.value}`}>
                    {d.label}{full ? ` (${c.fullTag})` : ""}
                  </option>
                );
              })}
            </select>
          </div>
        )}
        {tickets && (
          <div>
            <label htmlFor={`${offer}-ticket`} className={labelCls}>{c.ticketLabel}</label>
            <select id={`${offer}-ticket`} required value={form.ticket} onChange={set("ticket")} className={`${inputCls} cursor-pointer`} data-testid="booking-ticket-select">
              {tickets.map((t) => {
                const need = offer === "active-birth" && t.value === "couple" ? 2 : 1;
                const short = !!(availability && form.date && (availability[form.date] ?? 0) < need);
                return <option key={t.value} value={t.value} disabled={short}>{t.label}{short ? ` ${c.notEnough}` : ""}</option>;
              })}
            </select>
          </div>
        )}
        <div>
          <label htmlFor={`${offer}-name`} className={labelCls}>{c.name}</label>
          <input id={`${offer}-name`} required value={form.name} onChange={set("name")} className={inputCls} data-testid="booking-name-input" />
        </div>
        <div>
          <label htmlFor={`${offer}-email`} className={labelCls}>{c.email}</label>
          <input id={`${offer}-email`} type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="booking-email-input" />
        </div>
        <div>
          <label htmlFor={`${offer}-phone`} className={labelCls}>{c.phone}</label>
          <input id={`${offer}-phone`} value={form.phone} onChange={set("phone")} className={inputCls} data-testid="booking-phone-input" />
        </div>
      </div>
      <div className="mt-8 flex flex-wrap items-center gap-6">
        <button type="submit" disabled={submitting} data-testid="booking-submit"
          className="group inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust disabled:opacity-60">
          {submitting ? "…" : `${c.submit}${priceLabel ? ` — ${priceLabel}` : ""}`}
          <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </button>
      </div>
      <p className="mt-4 text-xs leading-relaxed text-soft/70 max-w-xl">{c.note}</p>
    </form>
  );
};

export const PlacesLeft = ({ offer, dates }) => {
  const { lang } = useLang();
  const c = BOOKING_COPY[lang] || BOOKING_COPY.en;
  const [remaining, setRemaining] = useState(null);
  useEffect(() => {
    fetch(`${API}/bookings/availability/${offer}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setRemaining(d.remaining))
      .catch(() => {});
  }, [offer]);
  if (!remaining) return null;
  return (
    <>
      {dates.map((d, i) => {
        const n = remaining[d.value];
        return (
          <span key={d.value} data-testid={`places-left-${d.value}`}>
            {i > 0 && " · "}
            {dates.length > 1 && `${d.short}: `}
            {n === 0 ? c.soldOutTag : c.placesLeft(n)}
          </span>
        );
      })}
    </>
  );
};

const RefundPolicy = ({ offer }) => {
  const { lang } = useLang();
  const c = (BOOKING_COPY[lang] || BOOKING_COPY.en).refund;
  const [open, setOpen] = useState(false);
  const rows = [...(offer === "womens-circle" ? c.itemsWomensCircle : c.items), ...(offer === "mums-babies" ? [c.circle] : []), ...c.itemsAfter];
  return (
    <div className="mt-10 border-t border-ink/10" data-testid="refund-policy">
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="flex w-full items-center justify-between gap-6 py-5 text-left" data-testid="refund-policy-toggle">
        <span className="text-xs tracking-[0.25em] uppercase text-soft">{c.title}</span>
        <ChevronDown size={16} strokeWidth={1.5} className={`shrink-0 text-clay transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} className="overflow-hidden">
            <div className="pb-6 max-w-xl">
              <p className="text-sm leading-relaxed text-soft">{c.intro}</p>
              {rows.map((r) => (
                <div key={r.h} className="mt-5">
                  <p className="text-sm font-medium text-ink">{r.h}</p>
                  <p className="mt-1 text-sm leading-relaxed text-soft">{r.b}</p>
                </div>
              ))}
              <p className="mt-5 text-xs leading-relaxed text-soft/70">{c.outro}</p>
              {offer === "active-birth" && (
                <p className="mt-4 text-xs leading-relaxed text-soft/70" data-testid="refund-statutory">{c.statutory}</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const BookingSection = ({ offer, dates, tickets, priceLabel, banner }) => {
  const { lang } = useLang();
  const c = BOOKING_COPY[lang] || BOOKING_COPY.en;
  return (
    <section id="book" className="bg-cream border-b border-ink/10 py-16 lg:py-20" data-testid="booking-section">
      <div className="mx-auto max-w-3xl px-6">
        <Reveal>
          {banner}
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink mb-4">{c.title}</h2>
          <BookingForm offer={offer} dates={dates} tickets={tickets} priceLabel={priceLabel} />
          <RefundPolicy offer={offer} />
        </Reveal>
      </div>
    </section>
  );
};
